import { createEntityAdapter, EntityAdapter, EntityState, Dictionary } from '@ngrx/entity';
import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { IKeg, IKegVM } from 'app/models/keg';
import * as Action from './actions'
import * as fromUnits from '../units/reducer';
import * as fromBeverage from '../beverage/reducer';
import * as fromLocation from '../location/reducer';
import * as fromRouter from '../router/router-state.selectors';

import { UnitsHelper } from 'app/services/units.helper';
import { IBeverage, IBeverageVM } from 'app/models/beverage';
import { ILocation } from 'app/models/location';
import * as fromCan from 'app/state/can/reducer';
import * as fromBottle from 'app/state/bottle/reducer';
import * as fromBarSettings from 'app/state/bar-settings/reducer';
import { SortService } from 'app/services/helpers/sort.service';
import { IBottle } from 'app/models/bottle';
import { ICan } from 'app/models/can';

export interface State extends EntityState<IKeg> {
  // additional entity state properties
  selectedId: string | null;
  error: string;
}


export function sortByOrder(a: IKeg, b: IKeg): number {
  const compare = a.order - b.order;
  if (compare > 0) {
    return 1;
  } else if (compare < 0) {
    return -1;
  } else {
    return 0;
  }
}

export function selectKegId(a: IKeg): string {
  return a.id ?? '';
}
export const adapter: EntityAdapter<IKeg> = createEntityAdapter<IKeg>({
  sortComparer: sortByOrder,
  selectId: selectKegId
});

export const initialState: State = adapter.getInitialState({
  selectedId: null,
  error: ''
});

export const stateName = 'keg';

export const reducer = createReducer<State>(
  initialState,
  on(Action.clearAllItems, (state) => {
    return adapter.removeAll(state);
  }),
  on(Action.loadItemsSuccess, (state, { items }) => {
    return adapter.setAll(items, state);
  }),
  on(Action.updateItem, (state, { item }) => {
    return adapter.setOne(item, state);
  }),
  on(
    Action.setItem,
    Action.setItemAndNavigate,
    (state, action) => Object.assign({ ...state, selectedId: action.id })),

  on(Action.setItemFromListPage,
    (state, action) => {
      return Object.assign({
        ...state, selectedId: action.id
      })
    })


);


//export const getSelectedId = (state: KegState) => state.selectedKegId;

// get the selectors
// get the selectors
const {
  selectAll,
  selectEntities: selectItemEntities,
  selectIds: selectItemIds,
  selectTotal
} = adapter.getSelectors();

// Export selectAll for effect caching
export { selectAll };

export const getSelectedId = (state: State) => state.selectedId;
const getState = createFeatureSelector<State>(stateName);
export const selectAllItems = createSelector(getState, selectAll);
export const selectAllEntities = createSelector(getState, selectItemEntities);
export const selectCurrentId = createSelector(getState, getSelectedId);
export const selectCurrentItem = createSelector(selectAllEntities, selectCurrentId, (entities, id) => {
  if (id) return entities[id];
  return null;
});

export const selectEntity = (id: string) => createSelector(selectAllEntities, entities => entities[id]);

export const getKegsWithUnit = createSelector(
  selectAllItems,
  fromUnits.getUnits,
  (kegs, units) => {
    if (units) {
      const helper = new UnitsHelper();
      const retKegs: IKeg[] = [];
      kegs.forEach(item => {
        if (!item) return;
        const retItem: IKeg = {
          ...item,
          size: !!item ? helper.toUnit(item.size, 'l', units.kegs, 2) : 0,
          amount: !!item ? helper.toUnit(item.amount, 'l', units.kegs, 2) : 0,
          nettoWeight: item && item.nettoWeight ? helper.toUnit(item.nettoWeight ?? 4, 'kg', units.kegWeight, -1) : 4,
        };
        retKegs.push(retItem);
      });

      return retKegs;
    }
    return kegs;
  });



export const getKegsWithUnitBeverageAndLocation = createSelector(
  getKegsWithUnit,
  fromBeverage.selectAllEntities,
  fromLocation.selectAllEntities,
  (kegs: IKegVM[], beverages: Dictionary<IBeverage>, locations: Dictionary<ILocation>) => {
    const retKegs: IKegVM[] = [];
    kegs.forEach(item => {
      const retItem: IKegVM = {
        ...item,
        location: item.locationId ? locations[item.locationId] : undefined,
        beverage: item.beverageId ? beverages[item.beverageId] : undefined,
      };
      retKegs.push(retItem);
    });
    return retKegs;
  });

export const getKegsWithUnitFromBeverage = (beverageId: string) => createSelector(
  getKegsWithUnit,
  (kegs) => {
    return kegs.filter(x => x.beverageId === beverageId);
  });

export const getKegWithUnits = (id: string) => createSelector(
  getKegsWithUnit,
  (kegs) => {
    return kegs.find(x => x.id === id);
  });



export const getBeverageWithKegs = (id: string) => createSelector(
  fromBeverage.selectEntity(id),
  getKegsWithUnit,
  (item, kegs): IBeverageVM | undefined => {
    if (item && kegs) {
      const retItem = { ...item };
      retItem.kegs = [];

      kegs.forEach(keg => {
        if (keg.beverageId === id) {
          retItem.kegs?.push(keg);
        }
      });
      return retItem;
    }

    if (item) {
      return item;
    }
    return undefined;
  }
);

export const getBeverageWithKegsFromRouteParams = createSelector(
  fromRouter.selectRouteParams,
  fromBeverage.selectAllEntities,
  getKegsWithUnit,
  ({ id }, items, kegs): IBeverageVM | undefined => {
    const item = items[id];
    if (item && kegs) {
      const retItem = { ...item };
      retItem.kegs = [];

      kegs.forEach(keg => {
        if (keg.beverageId === id) {
          retItem.kegs?.push(keg);
        }
      });
      return retItem;
    }

    if (item) {
      return item;
    }
    return undefined;
  }
);


export const getBeveragesWithKegs = createSelector(
  fromBeverage.selectAllItems,
  getKegsWithUnit,
  fromCan.selectAllItems,
  fromBottle.selectAllItems,
  (items: IBeverageVM[], kegs: IKeg[], cans: ICan[], bottles: IBottle[]) => {
    return items.map((item) => {
      return {
        ...item,
        kegs: kegs.filter(x => x.beverageId === item.id),
        cans: cans.filter(x => x.beverageId === item.id),
        bottles: bottles.filter(x => x.beverageId === item.id),
      }
    })
  }
);

export const selectBeveragesWithCansOrBottles = createSelector(
  getBeveragesWithKegs,
  (items: IBeverageVM[]) => {
    return items.filter(item => {
      return (item.cans?.length ?? 0 > 0) ||
        (item.bottles?.length ?? 0 > 0)
    });
  }
);


export const selectBeveragesForAllBeverages = createSelector(
  getBeveragesWithKegs,
  fromBarSettings.selecAllBeersFilter,
  fromBarSettings.selectCurrentItem,
  (items: IBeverageVM[], searchFilter, barsettings) => {


    items = items.filter(item => {
      // Parenthesize the `?? 0` — JS precedence makes `?? 0 > 0` parse as
      // `?? (0 > 0)`. It happens to work today because 0 is falsy, but only
      // by accident.
      return ((item.kegs?.length ?? 0) > 0) ||
        ((item.cans?.length ?? 0) > 0) ||
        ((item.bottles?.length ?? 0) > 0);
    });

    items = items.filter(item => {
      if (!searchFilter) return true;
      const q = searchFilter.toLowerCase();
      const matchesStyle = item.beerStyle?.name?.toLowerCase().includes(q) ?? false;
      const matchesName = item.name?.toLowerCase().includes(q) ?? false;
      return matchesStyle || matchesName;
    });



    return SortService.sortBeverageItems(items, barsettings?.allBeerSortOrder || 'name');
  }
);



export const selectBeveragesForFavoritesBeverages = createSelector(
  getBeveragesWithKegs,
  fromBarSettings.selectFavoriteBeersFilter,
  fromBarSettings.selectCurrentItem,
  (items: IBeverageVM[], searchFilter, barsettings) => {

    items = items.filter(item => {
      return (item?.favorite);
    });

    items = items.filter(item => {
      if (!searchFilter) return true;
      const q = searchFilter.toLowerCase();
      const matchesStyle = item.beerStyle?.name?.toLowerCase().includes(q) ?? false;
      const matchesName = item.name?.toLowerCase().includes(q) ?? false;
      return matchesStyle || matchesName;
    });

    return SortService.sortBeverageItems(items, barsettings?.favBeerSortOrder || 'name');
  }
);

export const getKegWithUnitsFromParams = createSelector(
  fromRouter.selectRouteParams,
  getKegsWithUnit,
  ({ id }, kegs) => {
    return kegs.find(x => x.id === id)
  });




export const getFromParams = createSelector(
  fromRouter.selectRouteParams,
  selectAllItems,
  ({ id }, kegs) => {
    return kegs.find(x => x.id === id)
  }
);


export const selectAllItemsFromCurrentLocation =
  createSelector(
    selectAllItems,
    fromLocation.selectCurrentId,
    (items, locationId) => {
      return items.filter(i => i.locationId === locationId) as IKeg[];
    });
